/**
 * Register Page
 * New user registration with validation
 */

import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { validateEmail, validatePassword, validateLength } from '../utils/validators'

export default function RegisterPage() {
  const navigate = useNavigate()
  const { register, isLoading } = useAuth()

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [apiError, setApiError] = useState<string>('')
  const [success, setSuccess] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
    if (apiError) {
      setApiError('')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setApiError('')
    setSuccess(false)

    // Validate form
    const newErrors: Record<string, string> = {}

    const emailError = validateEmail(formData.email)
    if (emailError) newErrors.email = emailError

    const passwordError = validatePassword(formData.password)
    if (passwordError) newErrors.password = passwordError

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = '密碼不一致'
    }

    if (formData.displayName) {
      const displayNameError = validateLength(formData.displayName, 1, 100, '顯示名稱')
      if (displayNameError) newErrors.displayName = displayNameError
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    try {
      await register({
        email: formData.email,
        password: formData.password,
        display_name: formData.displayName || undefined,
      })
      setSuccess(true)
      // T030: Redirect to dashboard after 5 seconds (not login)
      setTimeout(() => {
        navigate('/dashboard')
      }, 5000)
    } catch (error) {
      if (error instanceof Error) {
        setApiError(error.message)
      } else {
        setApiError('註冊失敗，請稍後再試')
      }
    }
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.title}>CyberLoan.ai</h1>
          <p style={styles.subtitle}>建立新帳號</p>
        </div>

        {success ? (
          <div style={styles.successBox}>
            <span style={styles.successIcon}>✓</span>
            <div>
              <p style={styles.successTitle}>註冊成功！</p>
              {/* T029: Updated success message with email verification guidance */}
              <p style={styles.successText}>請查收驗證郵件以啟用您的帳號</p>
              <p style={styles.successText}>5 秒後將自動跳轉至 Dashboard...</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={styles.form}>
            {apiError && (
              <div style={styles.errorBox}>
                <span style={styles.errorIcon}>⚠️</span>
                <span>{apiError}</span>
              </div>
            )}

            <div style={styles.formGroup}>
              <label htmlFor="email" style={styles.label}>
                電子郵件 <span style={styles.required}>*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  ...(errors.email ? styles.inputError : {}),
                }}
                placeholder="your@email.com"
                autoComplete="email"
                disabled={isLoading}
              />
              {errors.email && <span style={styles.errorText}>{errors.email}</span>}
            </div>

            <div style={styles.formGroup}>
              <label htmlFor="displayName" style={styles.label}>
                顯示名稱 <span style={styles.optional}>(選填)</span>
              </label>
              <input
                type="text"
                id="displayName"
                name="displayName"
                value={formData.displayName}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  ...(errors.displayName ? styles.inputError : {}),
                }}
                placeholder="您的名稱"
                autoComplete="name"
                disabled={isLoading}
              />
              {errors.displayName && <span style={styles.errorText}>{errors.displayName}</span>}
            </div>

            <div style={styles.formGroup}>
              <label htmlFor="password" style={styles.label}>
                密碼 <span style={styles.required}>*</span>
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  ...(errors.password ? styles.inputError : {}),
                }}
                placeholder="••••••••"
                autoComplete="new-password"
                disabled={isLoading}
              />
              {errors.password && <span style={styles.errorText}>{errors.password}</span>}
              <div style={styles.passwordHint}>
                <small style={styles.hintText}>
                  至少 8 字元，包含大小寫字母和數字
                </small>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label htmlFor="confirmPassword" style={styles.label}>
                確認密碼 <span style={styles.required}>*</span>
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  ...(errors.confirmPassword ? styles.inputError : {}),
                }}
                placeholder="••••••••"
                autoComplete="new-password"
                disabled={isLoading}
              />
              {errors.confirmPassword && <span style={styles.errorText}>{errors.confirmPassword}</span>}
            </div>

            <button type="submit" style={styles.submitButton} disabled={isLoading}>
              {isLoading ? '註冊中...' : '註冊'}
            </button>
          </form>
        )}

        <div style={styles.footer}>
          <span style={styles.footerText}>已經有帳號？</span>
          <Link to="/login" style={styles.link}>
            立即登入
          </Link>
        </div>
      </div>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
    padding: '20px',
  },
  card: {
    width: '100%',
    maxWidth: '450px',
    backgroundColor: 'white',
    borderRadius: '10px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    padding: '40px',
  },
  header: {
    textAlign: 'center',
    marginBottom: '30px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#2563eb',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '16px',
    color: '#666',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#333',
  },
  required: {
    color: '#dc2626',
  },
  optional: {
    color: '#999',
    fontSize: '12px',
    fontWeight: '400',
  },
  input: {
    padding: '12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  inputError: {
    borderColor: '#dc2626',
  },
  errorText: {
    fontSize: '12px',
    color: '#dc2626',
  },
  errorBox: {
    padding: '12px',
    backgroundColor: '#fee2e2',
    borderRadius: '5px',
    color: '#dc2626',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  errorIcon: {
    fontSize: '16px',
  },
  successBox: {
    padding: '20px',
    backgroundColor: '#dcfce7',
    borderRadius: '5px',
    color: '#15803d',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '20px',
  },
  successIcon: {
    fontSize: '24px',
    fontWeight: 'bold',
  },
  successTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  successText: {
    fontSize: '14px',
    marginBottom: '4px',
  },
  passwordHint: {
    marginTop: '4px',
  },
  hintText: {
    fontSize: '12px',
    color: '#666',
  },
  link: {
    color: '#2563eb',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
  },
  submitButton: {
    padding: '12px',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  footer: {
    marginTop: '30px',
    textAlign: 'center',
    fontSize: '14px',
  },
  footerText: {
    color: '#666',
    marginRight: '8px',
  },
}
